import { Button, Popover, PopoverContent, PopoverTrigger, Search } from '@ibsys/kit';
import { LayersIcon } from 'lucide-react';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSamples, type SampleFilters } from '@/entities/sample';
import { useDebouncedValue } from '@/shared/hooks';
import { useSampleActions } from '../../model/useSampleActions';
import { useSampleSection } from '../../model/sampleSectionContext';
import { SampleCreateForm } from './SampleCreateForm';
import { SampleItem } from './SampleItem';

/** Сколько выборок показывает перечень: длинный список сужается поиском по названию */
const SAMPLES_PAGE_LIMIT = 50;

type Props = {
  /** Текущий отбор списка — он уходит в новую выборку по фильтрам */
  snapshot: SampleFilters;
  /** Отбор задан: без него выборку по фильтрам создавать не из чего */
  hasSelection: boolean;
};

/**
 * Блок выборок в строке управления списком: выбранная выборка, перечень выборок с поиском,
 * переименованием и удалением, а также сохранение текущего состояния как новой выборки.
 * Место блока пресетов раздела диалогов — пресетов в разделе выборок нет.
 */
export const SamplesPopover: FC<Props> = ({ snapshot, hasSelection }) => {
  const { t } = useTranslation(['common', 'samples']);
  const { sampleId, sample, canAdmin, pickedIds } = useSampleSection();
  const { openSample, createSample, renameSample, deleteSample, isSaving } = useSampleActions();

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  // Удалённые выборки в перечень не идут: применить их нельзя — список по ним отдаёт отказ.
  const { data } = useSamples({
    limit: SAMPLES_PAGE_LIMIT,
    isDeleted: false,
    search: debouncedSearch || undefined,
  });
  const samples = data?.samples ?? [];

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      {/* Сжимается обёртка, а не сама кнопка: базовый `ibs:shrink-0` у Button классом не снимается */}
      <div className="flex min-w-0">
        <PopoverTrigger asChild>
          <Button
            variant={sample ? 'outline' : 'ghost'}
            size="sm"
            icon={<LayersIcon className="size-4" />}
            aria-label={t('samples:triggerLabel')}
            className="max-w-full"
          >
            <span className="truncate">{sample?.name ?? t('samples:triggerLabel')}</span>
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent align="end" className="min-w-80 p-3 flex flex-col gap-3">
        <h4 className="font-medium text-sm">{t('samples:title')}</h4>

        {/* Размер поля обычный: в сжатом иконка поиска выше поля и обрезается до кружка поверх подсказки */}
        <Search
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('samples:searchPlaceholder')}
        />

        {samples.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">{t('samples:empty')}</p>
        ) : (
          <div className="flex flex-col gap-2 max-h-72 overflow-auto">
            {samples.map((item) => (
              <SampleItem
                key={item.id}
                sample={item}
                isActive={item.id === sampleId}
                canAdmin={canAdmin}
                onApply={() => openSample(item.id)}
                onRename={(name) => renameSample(item, name)}
                onDelete={() => deleteSample(item.id, { isOpened: item.id === sampleId })}
              />
            ))}
          </div>
        )}

        {canAdmin && (
          <SampleCreateForm
            hasSelection={hasSelection}
            pickedCount={pickedIds.length}
            isPending={isSaving}
            onCreate={(name) => createSample({ name, filters: snapshot, conversationIds: pickedIds })}
          />
        )}
      </PopoverContent>
    </Popover>
  );
};
